import Vue from 'vue'
import API from '@/api/index.js'
import Store from '@/store/index.js'

/**
 * Enum for notification types.
 * @readonly
 * @enum {string}
 * @property {string} NOTIFICATION - General notification (note: 'notiification' may be a typo).
 * @property {string} TREATMENT - Notification related to treatments.
 * @property {string} STYLING - Notification related to styling.
 * @property {string} NEWS - News notification.
 * @property {string} COMMENT - Notification for comments.
 */
const NotificationType = Object.freeze({
  NOTIFICATION: 'notification',
  TREATMENT: 'treatment',
  STYLING: 'styling',
  NEWS: 'news',
  COMMENT: 'comment',
})

/**
 * Class representing a collection of notifications.
 * Handles fetching, storing, and querying notification items.
 */
class Notifications {
  _items = []
  _completed = false
  _query = {count: 20, page: 1}

  /**
   * Retrieves the IDs of all notifications.
   */
  get allIds () {
    return this._items.map(item => item.id)
  }

  /**
   * Indicates whether the fetch operation has been completed.
   * @returns {boolean} True if the fetch operation is complete, false otherwise.
   */
  get completed () {
    return this._completed
  }

  /**
   * Retrieves notifications published after the specified date.
   *
   * If the items list is empty and not yet completed, it fetches notifications first.
   *
   * @async
   * @param {Date} date - The date to compare notification publish times against.
   * @returns {Promise<Array<Object>>} A promise that resolves to an array of notification items published after the given date.
   */
  async getNotificationsAfter(date) {
    if (this._items.length === 0 && !this.completed)
      await this._fetch()

    return this._items.filter(item => item.publishedAt.getTime() > date.getTime())
  }

  async _fetch () {
    return Promise.resolve()
  }
}

/**
 * Represents a notification handler for news items.
 * Extends the base Notifications class to fetch and manage news notifications.
 *
 * @class
 * @extends Notifications
 */
class NewsNotifications extends Notifications {
  constructor() {
    super()

    this._query = {
      status: 4,
      count: 100,
      salon: '12000010',
      page: 1
    }
  }

  /**
   * Asynchronously fetches news items from the API and updates the internal items list.
   * Each news item is mapped to a notification object with relevant properties.
   * Marks the fetch operation as completed upon success.
   *
   * @async
   * @private
   * @returns {Promise<void>} Resolves when the fetch and update operations are complete.
   */
  async _fetch () {
    const response = await API.getNews(this._query)

    this._items = response.items.map(item => ({
      id: item.id,
      text: item.title,
      publishedAt: new Date(item.releaseAt),
      type: NotificationType.NEWS,
      read: false,
      context: item
    }))

    this._completed = true
  }
}

/**
 * SummaryNotifications extends the Notifications class to fetch and summarize unsent styling data notifications.
 * 
 * @class
 * @extends Notifications
 */
class SummaryNotifications extends Notifications {
  /**
   * Asynchronously fetches unsent styling data notifications from the API.
   * 
   * - Unsent treatment within 72 hours
   * - Updated at 8 AM
   * 
   * @async
   * @private
   * @returns {Promise<void>} Resolves when the fetch and update process is complete.
   */
  async _fetch () {
    const today = new Date()

    // If before 8 AM, adjust to the previous day
    if (today.getHours() < 8)
      today.setDate(today.getDate() - 1)

    // Set to 8 AM
    today.setHours(8, 0, 0, 0)

    // Calculate the limit date for unsent items (72 hours ago)
    const limitDate = new Date(today.getTime() - 72*3600*1000)
    const payload = {
      datestart: limitDate,
      count: 500
    }

    const response = await Promise.all([
      API.getItems(Object.assign({}, payload, {status:2})),
      API.getItems(Object.assign({}, payload, {status:3})),
    ])

    const unsentItems = []

    response.forEach(item =>
      unsentItems.push(
        ...item.items.filter(item => new Date(item.publishedAt).getTime() > limitDate.getTime())
      )
    )

    this._items = unsentItems.length
      ? [
          {
            id: today.getTime(),
            text: `未送信のスタイリングデータが${unsentItems.length}件あります。`,
            publishedAt: today,
            type: NotificationType.NOTIFICATION,
            read: false
          }
        ]
      : []

    this._completed = true
  }
}

/**
 * Handles fetching and processing of comment notifications for salons.
 * Extends the Notifications class to provide specialized logic for comment-related notifications.
 *
 * @class
 * @extends Notifications
 */
class CommentNotifications extends Notifications {
  constructor() {
    super()

    this._query = {
      type: 'salons',
    }
  }

  /**
   * Asynchronously fetches comments using the current store's ID and query type,
   * filters for items with status 4, maps them into notification objects,
   * and stores up to 100 notifications in the `_items` property.
   * Marks the fetch as completed by setting `_completed` to true.
   *
   * @async
   * @private
   * @returns {Promise<void>} Resolves when the fetch and processing are complete.
   */
  async _fetch () {
    const id = Store.state.id
    const response = await API.getComments(id, this._query.type)

    this._items = response
      .filter(item => item.status === 4)
      .map(item => ({
        id: item.id,
        text: `「${item.message.substring(0, 10)}...」というテンプレートコメントが追加されましたのでご確認ください。`,
        type: NotificationType.COMMENT,
        publishedAt: new Date(item.releaseAt),
        read: false,
        context: item
      }))
      .slice(0, 100)

    this._completed = true
  }
}

/**
 * LikeNotifications manages and fetches notifications related to "likes" on styles.
 * 
 * Extends the Notifications class to provide functionality for:
 * - Paginated fetching of liked notifications from an API.
 * - Determining if enough notifications have been loaded based on a date.
 * - Filtering notifications published after a specific date.
 * 
 * @class
 * @extends Notifications
 */
class LikeNotifications extends Notifications {
  constructor() {
    super()

    this._query = {
      count: 20,
      page: 0
    }
  }

  /**
   * Determines if there are enough items based on the provided date.
   *
   * Returns true if the operation is already completed, or if there is at least one item
   * and the last item's publishedAt date is earlier than or equal to the given date.
   *
   * @param {Date} date - The date to compare against the last item's publishedAt property.
   * @returns {boolean} True if there are enough items or the operation is completed, otherwise false.
   */
  hasEnoughItems (date) {
    return this.completed
      || (
        this._items.length
        && this._items[this._items.length - 1].publishedAt.getTime() <= date.getTime()
      )
  }

  /**
   * Fetch notifications that were published after the given date.
   *
   * @param {Date} date - The date to compare against the publishedAt property.
   * @returns {Promise<Array>} A promise that resolves to an array of notifications.
   */
  async getNotificationsAfter(date) {
    let limit = 5

    while (!this.hasEnoughItems(date) && limit--)
      await this._fetch()

    return this._items.filter(item => item.publishedAt.getTime() > date.getTime())
  }

  /**
   * Fetch liked notifications.
   *
   * @returns {Promise<void>}
   */
  async _fetch () {
    this._query.page++

    return  API.getLikedItems(this._query).then(async response => {
      if (!response.items.length) {
        this._completed = true
        return
      }

      // Fetch customer information
      const customers = await this._fetchCustomers(response.items.map(item => item.customer))

      this._items.push(
        ...response.items.map(item => ({
          id: item.id,
          text: (customers[item.customer] ? `${customers[item.customer].name}さんから` : '')
                  + 'スタイルに「いいね！」が届きました。',
          publishedAt: new Date(item.likedAt),
          read: item.statusByUser.islikeRead,
          type: NotificationType.STYLING,
          context: item
        }))
      )

      if (this._items.length >= Number(response.total)) {
        this._completed = true
      }
    })
  }

  /**
   * Fetches customer data for the given list of item IDs.
   *
   * @async
   * @param {Array<string|number>} ids - An array of customer IDs to fetch.
   * @returns {Promise<Object>} A promise that resolves to an object mapping customer IDs to customer data.
   */
  async _fetchCustomers (ids) {
    return (await API.getCustomers({
      id: [...new Set(ids)].join(','),
      count: ids.length
    })).items.reduce((acc, cur) => {
      acc[cur.id] = cur
      return acc
    }, {})
  }
}

const state = () => {
  return {
    /**
     * All items
     * @property {string} [].id - Notification ID
     * @property {string} [].text - Notification text
     * @property {date} [].publishedAt - Published date and time
     * @property {string} [].type - Notification type [notification|treatment|comment|news|styling]
     * @property {boolean} [].read - Indicate the item is read
     */
    all: [],
    /** Summary notifications */
    summary: new SummaryNotifications(),
    /** News notifications */
    news: new NewsNotifications(),
    /** Comment notifications */
    comments: new CommentNotifications(),
    /** Like notifications */
    likes: new LikeNotifications(),
    /** Array of read notification ID */
    read: []
  }
}

// getters
const getters = {
  /** @returns all items */
  all: state => state.all,
  /** All unread items */
  unread: state => state.all.filter(item => !item.read),
  /** All read items */
  read: state => state.all.filter(item => item.read),
}

// actions
const actions = {
  /**
   * Update all notifications below
   * - Unsent Treatments
   * - New staffs
   * - Promoted staffs
   * - New template comments
   */
  async update ({state, commit, dispatch}) {
    try {
      // 28 days ago
      const limitDate = new Date(new Date().getTime() - 100*24*3600*1000)
      // Unsent items within 4 weeks
      const items = await dispatch('fetchUnsentTreatments')
      // Customers included in the items
      const customers = items.length ? await dispatch('fetchCustomers', {items: items.map(item => item.customer)}) : {}
      // Notifications
      const notifications = []

      // Fetch notifications
      await Promise.all([
        // Add summary notifications
        state.summary.getNotificationsAfter(limitDate),
        // Add news notifications
        state.news.getNotificationsAfter(limitDate),
        // Add comment notifications
        state.comments.getNotificationsAfter(limitDate),
        // Add like notifications
        state.likes.getNotificationsAfter(limitDate)
      ])
        .then(items => items.forEach(items => notifications.push(...items)))

      // Unsent treatments (within 72 hours)
      items.forEach(item => (
        (new Date().getTime() - new Date(item.publishedAt).getTime() > 72*3600*1000) &&
          notifications.push({
            id: item.id,
            text: `登録から72時間経過した${customers[item.customer] ?  customers[item.customer].name + 'さんの' : ''}スタイリングデータがございます。24時間後に送信ができなくなりますのでお早めに送信ください。`,
            type: 'treatment',
            // 72 hours after published
            publishedAt: new Date(new Date(item.publishedAt).getTime() + 72*3600*1000),
            read: false
          })
      ))

      // Update all
      commit('all', {items: notifications})
    } catch (error) {
      // Do notiong for now
      console.error(error)
    }
  },

  /** Fetch unsent treatments */
  async fetchUnsentTreatments ({rootState}) {
    // Today
    const today = new Date(new Date().setHours(8, 0, 0, 0))
    // Date limit
    const limit = new Date(new Date().getTime() - 28*24*3600*1000)
    // Search payload
    // Unsent treatments within 4weeks
    const payload = {
      // Within 5 days
      datestart: `${limit.getFullYear()}-${(limit.getMonth()+1).toString().padStart(2, 0)}-${limit.getDate().toString().padStart(2, 0)}`,
      // 500 items
      counts: 500
    }

    // Add user ID
    // Don't add id parameter if current user is an admin
    // Admin needs to receive all treatment notifications
    if (!rootState.staffs.me.isAdministrator)
      payload.id = rootState.staffs.me.id

    // Fetch all items
    return (await Promise.all([
      API.getItems(Object.assign({}, payload, {status:2})),
      API.getItems(Object.assign({}, payload, {status:3})),
      API.getItems(Object.assign({}, payload, {status:4}))
    ])).reduce((acc, cur) => {
      // Narrow down to before 8:00AM and overdue
      cur.items.forEach(item => {
        new Date(item.publishedAt).getTime() < today.getTime() &&
        (!Array.isArray(item.data) && (item.data.overdue === undefined || item.data.overdue)) &&
          acc.push(item)
      })
      return acc
    }, [])
  },

  /**
   * Fetch customer information specified in payload
   * @returns {object} Keys are customer ID, values are customer information
   */
  async fetchCustomers (context, {items}) {
    return (await API.getCustomers({
      id: [...new Set(items)].join(','),
      count: items.length
    })).items.reduce((acc, cur) => {
      acc[cur.id] = cur
      return acc
    }, {})
  }
}

// mutations
const mutations = {
  /** Clear the collection and add new items */
  all (state, {items}) {
    Vue.set(state, 'all', items.sort((a, b) => b.publishedAt.getTime() - a.publishedAt.getTime()))
  },

  /** Turn read flag of each item on */
  read (state, {items}) {
    items.forEach(item => {
      (j => j && Object.assign(j, {read:true}))(state.all.find(i => i.id === item.id))
    })
  },

  /**
   * Reset the store
   */
  reset (oldState) {
    Object.assign(oldState, state())
  }
}

export const create = () => ({
  namespaced: true,
  state,
  getters,
  actions,
  mutations
})

export default create()